import { ChangeDetectorRef, Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SettingsEntry } from '../../components/pages/settings.page';
import { ArrayHelpers, TranslateFormatText } from '../../helpers/array.helpers';
import { InjectorHelper } from '../../helpers/injector.helper';
import { TranslatedString } from '../../models/translatedstring.model';
import { SelfServiceSecurityService } from '../../services/datamodel.service';
import { MetaService } from '../../services/meta.service';
import { NavigationService } from '../../services/navigation.service';
import { RolesService } from '../../services/roles.service';
import { BaseListDetail, BaseListSettings, DeleteTexts, SaveTexts } from '../base.list.settings';
import { ContainerSecuritySettings } from './container/container.settings';
import { EnumSecuritySettings } from './enumSec/enumsec.settings';
import { PermissionsPage } from './permissions/permissions.page';

@Component({
    selector: 'roles-settings',
    templateUrl: '../base.list.settings.html'
})
export class RoleSettings extends BaseListSettings {

    private static _RoleNode: SettingsEntry = {
        Caption: '@@Rollen',
        ID: 'roles',
        Icon: 'shield',
        Index: 2,
        Parent: 'userCont',
        Security: {
            Name: 'evidanza.App.Shared.Security.UsermanagementRight',
            Value: 8
        },
        Children: [],
        UpdateFunction: RoleSettings.UpdateRoles
    };

    static GetSettingsEntry() {
        return RoleSettings._RoleNode;
    }

    static UpdateRoles() {
        return new Promise<void>(resolve => {
            RoleSettings._RoleNode.Children = [];
            const ms = InjectorHelper.InjectorInstance.get<MetaService>(MetaService);
            ms.GetNavigationStructures().subscribe(ns => {
                const rs = InjectorHelper.InjectorInstance.get<RolesService>(RolesService);
                rs.ReadRoleInfos().subscribe(result => {
                    const list = [];
                    let index = 0;
                    list.push({
                        Caption: '@@Uebersicht',
                        ID: 'role-overview',
                        Icon: 'menu',
                        Index: index++,
                        Content: RoleSettings
                    });
                    if (result) {
                        const roleList = [];
                        result.forEach((role) => {
                            const node = RoleSettings.CreateRoleNode(role, ns);
                            node['Index'] = index++;
                            roleList.push(node);
                        });
                        ArrayHelpers.sortAlphabetical(roleList, 'Caption');
                        list.push(...roleList);
                    }
                    RoleSettings._RoleNode.Children = list;
                    resolve();
                });
            });
        });
    }

    private static CreateRoleNode(role, ns) {
        const retVal = {
            Caption: role.Name,
            ID: role.Id,
            Icon: 'radio_button_unchecked',
            Children: []
        };
        retVal.Children.push({
            Caption: '@@Berechtigungen',
            ID: 'enums',
            Icon: 'security',
            Index: 0,
            Content: EnumSecuritySettings,
            InitArgs: role
        });
        if (!RolesService.IsSystemRole(role.Id)) {
            //retVal.Children.push({
            //    Caption: '@@Integrierte Rollen',
            //    ID: role.Id + '_childRoles',
            //    Icon: 'list',
            //    Index: 1,
            //    Content: ChildRoleSettings,
            //    InitArgs: role
            //});
            if (ns && ns.length > 0) {
                const layoutNode = {
                    Caption: '@@Layout',
                    ID: 'layout',
                    Icon: 'aspect_ratio',
                    Index: 2,
                    Children: []
                };
                ns.forEach((x, i) => {
                    layoutNode.Children.push({
                        Caption: x.Caption,
                        ID: x.SID,
                        Icon: 'radio_button_unchecked',
                        Index: i,
                        Content: PermissionsPage,
                        InitArgs: {
                            RoleID: role.Id,
                            StructureID: x.SID,
                            StructureKey: x.Caption
                        }
                    });
                });
                retVal.Children.push(layoutNode);
            }
            retVal.Children.push({
                Caption: '@@Container',
                ID: 'container',
                Icon: 'description',
                Index: 3,
                Content: ContainerSecuritySettings,
                InitArgs: role
            });
        }
        return retVal;
    }

    constructor(private rolesService: RolesService, private translate: TranslateService,
        protected cdRef: ChangeDetectorRef, private securityService: SelfServiceSecurityService) {
        super(cdRef);
        this.ShowSave = false;
        this.ShowDelete = false;
    }

    getContentType() {
        return RoleDetail;
    }

    loadList(handler) {
        this.rolesService.ReadRoleInfos().subscribe(result => {
            if (result) {
                const list = [];
                result.forEach((role) => {
                    list.push({ Caption: role.Name, ID: role.Id });
                });
                handler(list);
            }
        });
    }

    loadData(data) {
        this.rolesService.FindRole(data).subscribe(result => {
            if (result) {
                this.setSelectedItem(result);
            }
        });
    }

    setSelectedItem(item) {
        super.setSelectedItem(item);
        if (!item || RolesService.IsSystemRole(item.SID)) {
            this.ShowSave = false;
            this.ShowDelete = false;
        } else {
            this.ShowSave = true;
            this.ShowDelete = true;
        }
    }

    getNewItem() {
        return {
            Name: new TranslatedString(this.translate.instant('@@Neue Rolle')),
            IsAdministrator: false
        };
    }

    getDeleteText(sel): DeleteTexts {
        const retVal = new DeleteTexts();
        retVal.Question = new TranslateFormatText('@@Sind Sie sicher, dass Sie die Rolle \'{0}\' loeschen moechten?');
        retVal.Question.FormatParams.push(sel.Caption);
        retVal.Success = new TranslateFormatText('@@Rolle \'{0}\' erfolgreich geloescht.');
        retVal.Success.FormatParams.push(sel.Caption);
        retVal.Title = new TranslateFormatText('@@Rolle loeschen');
        return retVal;
    }

    getSaveSuccessText(sel): SaveTexts {
        let caption = TranslatedString.GetTranslation(sel.Name);
        if (!caption) {
            caption = sel.SID;
        }
        const retVal = new SaveTexts();
        retVal.Text = new TranslateFormatText('@@Rolle \'{0}\' erfolgreich gespeichert.');
        retVal.Text.FormatParams.push(caption);
        retVal.Title = new TranslateFormatText('@@Rolle speichern');
        return retVal;
    }

    delete(data, handler) {
        this.securityService.DeleteCompleteRole(data).subscribe((res) => {
            handler(res);
            NavigationService.Refresh.next(RoleSettings._RoleNode.ID);
        });
    }

    saveInternal(item, handler) {
        this.rolesService.AddOrUpdateRole({ Role: item }).subscribe(result => {
            if (result) {
                handler(result, result.SID, result.Caption);
            }
        });
    }

    handleNew(item, result) {
        item.SID = result.SID;
        item.Version = result.Version;
        NavigationService.Refresh.next(RoleSettings._RoleNode.ID);
    }
}

@Component({
    selector: 'roles-detail',
    templateUrl: './roles.settings.html',
    styleUrls: ['./roles.settings.css']
})
export class RoleDetail extends BaseListDetail {
    CanSave = false;

    setSelectedItem(item) {
        super.setSelectedItem(item);
        this.CanSave = item && !RolesService.IsSystemRole(item.SID);
    }
}
